/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiEdit2,
  FiMapPin,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiTruck,
  FiX
} from "react-icons/fi";
import Swal from "sweetalert2";
import WhatsLayout from "../layouts/WhatsLayout";
import { useUI } from "../theme/ThemeProvider";
import { useTenantStorePermissions } from "../../hooks/useTenantStorePermissions";
import NoAccess from "../components/NoAccess";
import { useAuth } from "../../context/auth/AuthContext";
import {
  addDeliveryCompany,
  deleteDeliveryCompany,
  getCities,
  getDeliveryCompanies,
  updateDeliveryCompany
} from "../../Stores/utils/storeApi";

const sanitizeFeeInput = (value) => {
  const normalizedValue = String(value).replace(/,/g, ".").replace(/[^\d.]/g, "");
  if (!normalizedValue) return "";

  const [integerPart = "", ...decimalParts] = normalizedValue.split(".");
  if (!decimalParts.length) return integerPart;

  return `${integerPart}.${decimalParts.join("")}`;
};

const buildCityFees = (cities, company = null) => {
  const feesMap = new Map(
    (company?.cities || []).map((city) => [Number(city.cityId), city.fees ?? ""])
  );

  return cities.map((city) => ({
    cityId: Number(city.cityId),
    cityName: city.cityName,
    fees: feesMap.has(Number(city.cityId)) ? String(feesMap.get(Number(city.cityId))) : "0"
  }));
};

export default function AdminDeliveryCompanies() {
  const { theme, lang, colors } = useUI();
  const { user } = useAuth();
  const tenantId = user?.tenantId;
  const isDark = theme === "dark";
  const isArabic = lang === "ar";
  const perms = useTenantStorePermissions();

  const [companies, setCompanies] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [expandedCompanies, setExpandedCompanies] = useState({});
  const [formData, setFormData] = useState({
    deliveryCompanyName: "",
    isActive: false,
    cities: []
  });

  const activeCompanies = useMemo(
    () => companies.filter((company) => company.isActive),
    [companies]
  );

  const filteredCompanies = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return companies;

    return companies.filter((company) => {
      const nameMatch = company.deliveryCompanyName.toLowerCase().includes(query);
      const cityMatch = company.cities.some((city) =>
        city.cityName.toLowerCase().includes(query)
      );
      return nameMatch || cityMatch;
    });
  }, [companies, searchTerm]);

  const loadData = async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const [companyData, citiesResponse] = await Promise.all([
        getDeliveryCompanies(tenantId),
        getCities()
      ]);
      setCompanies(Array.isArray(companyData) ? companyData : []);
      setCities(citiesResponse.success ? citiesResponse.data : []);
    } catch (error) {
      console.error("Failed to load delivery companies", error);
      setCompanies([]);
      setCities([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [tenantId]);

  const resetForm = (company = null) => {
    setEditingCompany(company);
    setFormData({
      deliveryCompanyName: company?.deliveryCompanyName || "",
      isActive: !!company?.isActive,
      cities: buildCityFees(cities, company)
    });
  };

  const handleOpenModal = (company = null) => {
    resetForm(company);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCompany(null);
  };

  const toggleCompanyCities = (companyKey) => {
    setExpandedCompanies((prev) => ({
      ...prev,
      [companyKey]: !prev[companyKey]
    }));
  };

  const handleCityFeeChange = (cityId, value) => {
    const sanitizedValue = sanitizeFeeInput(value);
    setFormData((prev) => ({
      ...prev,
      cities: prev.cities.map((city) =>
        city.cityId === cityId ? { ...city, fees: sanitizedValue } : city
      )
    }));
  };

  const handleCityFeePaste = (cityId, event) => {
    const pastedValue = event.clipboardData.getData("text");
    const sanitizedValue = sanitizeFeeInput(pastedValue);
    if (sanitizedValue === "") return;

    event.preventDefault();
    handleCityFeeChange(cityId, sanitizedValue);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const companyName = formData.deliveryCompanyName.trim();
    if (!companyName) {
      Swal.fire(
        isArabic ? "خطأ" : "Error",
        isArabic ? "يرجى إدخال اسم الشركة" : "Please enter a company name",
        "error"
      );
      return;
    }

    if (formData.cities.length !== cities.length) {
      Swal.fire(
        isArabic ? "خطأ" : "Error",
        isArabic
          ? "يجب إدخال جميع المحافظات مع الأسعار"
          : "All cities must be filled with fees",
        "error"
      );
      return;
    }

    const anotherActiveCompany = companies.find(
      (company) =>
        company.isActive &&
        company.companyKey !== editingCompany?.companyKey
    );

    if (formData.isActive && anotherActiveCompany) {
      Swal.fire(
        isArabic ? "تنبيه" : "Warning",
        isArabic
          ? "لا يمكن تفعيل أكثر من شركة توصيل واحدة. قم بإلغاء تفعيل الشركة النشطة أولًا."
          : "Only one delivery company can be active. Disable the current active company first.",
        "warning"
      );
      return;
    }

    const payload = {
      deliveryCompanyName: companyName,
      isActive: !!formData.isActive,
      cities: formData.cities.map((city) => ({
        cityId: city.cityId,
        cityName: city.cityName,
        fees: Number(city.fees || 0)
      }))
    };

    const success = editingCompany
      ? await updateDeliveryCompany(tenantId, payload)
      : await addDeliveryCompany(tenantId, payload);

    if (!success) {
      Swal.fire(
        isArabic ? "فشل" : "Failed",
        isArabic ? "حدث خطأ أثناء الحفظ" : "Error while saving",
        "error"
      );
      return;
    }

    Swal.fire(
      isArabic ? "تم بنجاح" : "Success",
      isArabic ? "تم حفظ بيانات الشركة" : "Delivery company saved successfully",
      "success"
    );
    handleCloseModal();
    loadData();
  };

  const handleDelete = async (company) => {
    const confirmation = await Swal.fire({
      title: isArabic ? "هل أنت متأكد؟" : "Are you sure?",
      text: isArabic
        ? "سيتم حذف الشركة من جميع المحافظات."
        : "This will delete the company for all cities.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: colors.mainColor,
      cancelButtonColor: colors.red,
      confirmButtonText: isArabic ? "نعم، احذف" : "Yes, delete it",
      cancelButtonText: isArabic ? "إلغاء" : "Cancel"
    });

    if (!confirmation.isConfirmed) return;

    const success = await deleteDeliveryCompany(tenantId, company.deliveryIds);
    if (!success) {
      Swal.fire(
        isArabic ? "فشل" : "Failed",
        isArabic ? "حدث خطأ أثناء الحذف" : "Error while deleting",
        "error"
      );
      return;
    }

    Swal.fire(
      isArabic ? "تمت الإزالة" : "Deleted",
      isArabic ? "تم حذف الشركة من جميع المحافظات" : "Company deleted from all cities",
      "success"
    );
    loadData();
  };

  if (!perms.viewShipping) return <NoAccess />;

  return (
    <WhatsLayout
      title={isArabic ? "شركات التوصيل" : "Delivery Companies"}
      hideNavbar={showModal}
    >
      <section
        dir={isArabic ? "rtl" : "ltr"}
        onContextMenu={(e) => e.stopPropagation()}
        onCopy={(e) => e.stopPropagation()}
        onSelectStart={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          // Allow standard shortcuts (Ctrl+A, C, V, X) by stopping propagation to the protection script
          const key = e.key.toLowerCase();
          if (e.ctrlKey && ["a", "c", "v", "x"].includes(key)) e.stopPropagation();
        }}
        className={`min-h-screen px-5 sm:px-10 py-10 transition-all duration-300 ${isDark ? "text-gray-100" : "text-gray-900"
          }`}
        style={{
          background: isDark
            ? "linear-gradient(180deg, #0B1020 0%, #131827 100%)"
            : colors.bg
        }}
      >
        <div className="max-w-7xl mx-auto space-y-6">
          {activeCompanies.length > 1 && (
            <div className="rounded-3xl border border-amber-400/30 bg-amber-500/10 px-5 py-4 flex items-start gap-3">
              <FiAlertTriangle className="mt-1 text-amber-400" size={18} />
              <div>
                <p className="font-black">
                  {isArabic
                    ? "يوجد أكثر من شركة توصيل مفعلة حاليًا"
                    : "More than one delivery company is currently active"}
                </p>
                <p className="text-sm opacity-80">
                  {isArabic
                    ? "يجب أن تبقى شركة واحدة فقط بحالة نشطة."
                    : "Only one delivery company should remain active."}
                </p>
              </div>
            </div>
          )}

          <div className="rounded-[2rem] border p-5 sm:p-6 shadow-xl backdrop-blur-md bg-white/80 dark:bg-[#1E293B]/70 border-gray-200 dark:border-[#334155]">
            <div className="flex flex-col lg:flex-row justify-between gap-4 lg:items-center">
              <div className="relative w-full lg:max-w-md">
                <FiSearch
                  className="absolute top-1/2 left-4 -translate-y-1/2 opacity-50"
                  size={20}
                />
                <input
                  type="text"
                  placeholder={
                    isArabic
                      ? "ابحث باسم الشركة أو المحافظة..."
                      : "Search by company or city..."
                  }
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full py-3 px-12 rounded-2xl border transition-all outline-none ${isDark
                      ? "bg-[#0B1020] border-[#334155] focus:border-blue-500"
                      : "bg-white border-gray-200 focus:border-blue-400"
                    }`}
                />
              </div>

              {perms.addShipping && (
                <button
                  onClick={() => handleOpenModal()}
                  className="flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold shadow-lg transition-transform hover:scale-[1.02]"
                  style={{ background: colors.mainColor, color: "white" }}
                >
                  <FiPlus size={20} />
                  {isArabic ? "إضافة شركة" : "Add Company"}
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div
                className="animate-spin rounded-full h-12 w-12 border-b-2"
                style={{ borderBottomColor: colors.mainColor }}
              />
            </div>
          ) : filteredCompanies.length === 0 ? (
            <div className="text-center py-20 opacity-50">
              <FiTruck size={60} className="mx-auto mb-4" />
              <p className="text-xl">
                {isArabic
                  ? "لا يوجد شركات توصيل مضافة"
                  : "No delivery companies found"}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <AnimatePresence>
                {filteredCompanies.map((company) => {
                  const fees = company.cities.map((city) => Number(city.fees || 0));
                  const minFee = fees.length ? Math.min(...fees) : 0;
                  const maxFee = fees.length ? Math.max(...fees) : 0;
                  const isExpanded = !!expandedCompanies[company.companyKey];
                  const visibleCities = isExpanded
                    ? company.cities
                    : company.cities.slice(0, 6);

                  return (
                    <motion.div
                      key={company.companyKey}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.98 }}
                      className={`rounded-[2rem] border p-6 shadow-xl backdrop-blur-md ${isDark
                          ? "bg-[#1E293B]/70 border-[#334155]"
                          : "bg-white/90 border-gray-200"
                        }`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className="p-4 rounded-2xl bg-blue-500/10 text-blue-500">
                            <FiTruck size={28} />
                          </div>
                          <div>
                            <h3 className="text-xl font-black break-words">
                              {company.deliveryCompanyName}
                            </h3>
                            <div className="mt-2 flex flex-wrap gap-2">
                              <span
                                className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-black ${company.isActive
                                    ? "bg-emerald-500/10 text-emerald-500"
                                    : "bg-gray-500/10 text-gray-500"
                                  }`}
                              >
                                <FiCheckCircle size={14} />
                                {company.isActive
                                  ? isArabic
                                    ? "نشطة"
                                    : "Active"
                                  : isArabic
                                    ? "غير نشطة"
                                    : "Inactive"}
                              </span>
                              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold bg-purple-500/10 text-purple-500">
                                <FiMapPin size={14} />
                                {company.cities.length}{" "}
                                {isArabic ? "محافظة" : "Cities"}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="text-sm opacity-80 sm:text-end">
                          <p>
                            {isArabic ? "أقل رسوم" : "Min fee"}:{" "}
                            <span className="font-black">{minFee}</span>
                          </p>
                          <p>
                            {isArabic ? "أعلى رسوم" : "Max fee"}:{" "}
                            <span className="font-black">{maxFee}</span>
                          </p>
                        </div>
                      </div>

                      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {visibleCities.map((city) => (
                          <div
                            key={`${company.companyKey}-${city.cityId}`}
                            className={`rounded-2xl border px-4 py-3 ${isDark
                                ? "bg-[#0B1020] border-[#334155]"
                                : "bg-gray-50 border-gray-100"
                              }`}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <span className="font-bold">{city.cityName}</span>
                              <span className="text-sm font-black">
                                {Number(city.fees).toLocaleString()}{" "}
                                {isArabic ? "د.ع" : "IQD"}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>

                      {company.cities.length > 6 && (
                        <button
                          type="button"
                          onClick={() => toggleCompanyCities(company.companyKey)}
                          className="mt-3 text-xs opacity-70 hover:opacity-100 hover:text-blue-500 transition-colors"
                        >
                          {isExpanded
                            ? isArabic
                              ? "\u0625\u062e\u0641\u0627\u0621 \u0628\u0627\u0642\u064a \u0627\u0644\u0645\u062d\u0627\u0641\u0638\u0627\u062a"
                              : "Show fewer cities"
                            : isArabic
                              ? `\u0648 ${company.cities.length - 6} \u0645\u062d\u0627\u0641\u0638\u0627\u062a \u0623\u062e\u0631\u0649`
                              : `and ${company.cities.length - 6} more cities`}
                        </button>
                      )}

                      <div className="mt-6 flex items-center justify-between border-t border-dashed border-gray-500/20 pt-4">
                        {perms.editShipping ? (
                          <button
                            onClick={() => handleOpenModal(company)}
                            className="flex items-center gap-2 hover:text-blue-500 transition-colors"
                          >
                            <FiEdit2 />
                            {isArabic ? "تعديل" : "Edit"}
                          </button>
                        ) : (
                          <span />
                        )}

                        {perms.deleteShipping && (
                          <button
                            onClick={() => handleDelete(company)}
                            className="flex items-center gap-2 text-red-500 hover:text-red-600 transition-colors"
                          >
                            <FiTrash2 />
                            {isArabic ? "حذف" : "Delete"}
                          </button>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </div>

        {showModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className={`w-full max-w-5xl rounded-[2rem] p-6 sm:p-8 shadow-2xl max-h-[92vh] overflow-y-auto ${isDark ? "bg-[#1E293B]" : "bg-white"
                }`}
            >
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-black">
                    {editingCompany
                      ? isArabic
                        ? "تعديل شركة التوصيل"
                        : "Edit Delivery Company"
                      : isArabic
                        ? "إضافة شركة توصيل"
                        : "Add Delivery Company"}
                  </h2>
                  <p className="text-sm opacity-70 mt-1">
                    {isArabic
                      ? "يجب إدخال جميع المحافظات مع الرسوم، ويمكن تفعيل شركة واحدة فقط."
                      : "All cities must be filled with fees, and only one company can be active."}
                  </p>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="opacity-50 hover:opacity-100"
                >
                  <FiX size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2 opacity-70">
                      {isArabic ? "اسم الشركة" : "Company Name"}
                    </label>
                    <input
                      required
                      type="text"
                      value={formData.deliveryCompanyName}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          deliveryCompanyName: e.target.value
                        }))
                      }
                      className={`w-full p-4 rounded-xl border outline-none ${isDark
                          ? "bg-[#0B1020] border-[#334155]"
                          : "bg-gray-50 border-gray-200"
                        }`}
                    />
                  </div>

                  <div
                    className={`rounded-[1.5rem] border p-4 sm:p-5 ${isDark
                        ? "bg-[#0B1020] border-[#334155]"
                        : "bg-gradient-to-r from-gray-50 via-white to-gray-50 border-gray-200"
                      }`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                          <p className="font-black text-base">
                            {isArabic ? "حالة الشركة" : "Company Status"}
                          </p>
                        </div>
                        <p className="text-sm opacity-70 mt-1 leading-6 max-w-2xl">
                          {isArabic
                            ? "إذا لم تكن نشطة فلن تظهر في التوصيل أو في اختيار شركة الشحن."
                            : "If inactive, it will not appear in delivery selection."}
                        </p>
                      </div>

                      <div
                        className={`rounded-2xl border px-4 py-3 lg:min-w-[280px] ${isDark
                            ? "bg-[#111827] border-[#334155]"
                            : "bg-white border-gray-200 shadow-sm shadow-gray-200/60"
                          }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="text-xs opacity-60 font-bold">
                              {isArabic ? "الحالة الحالية" : "Current Status"}
                            </p>
                            <span
                              className={`inline-flex mt-1 px-2.5 py-1 rounded-full text-xs font-black ${formData.isActive
                                  ? "bg-emerald-500/10 text-emerald-600"
                                  : "bg-gray-500/10 text-gray-500"
                                }`}
                            >
                              {formData.isActive
                                ? isArabic
                                  ? "نشطة"
                                  : "Active"
                                : isArabic
                                  ? "غير نشطة"
                                  : "Inactive"}
                            </span>
                          </div>

                          <button
                            type="button"
                            onClick={() =>
                              setFormData((prev) => ({
                                ...prev,
                                isActive: !prev.isActive
                              }))
                            }
                            className={`relative h-9 w-[68px] rounded-full transition-all duration-300 ${formData.isActive
                                ? "bg-emerald-500 shadow-lg shadow-emerald-500/20"
                                : "bg-gray-300"
                              }`}
                            aria-label={isArabic ? "تبديل حالة الشركة" : "Toggle company status"}
                          >
                            <span
                              className={`absolute top-1.5 h-6 w-6 rounded-full bg-white shadow-md transition-transform duration-300 ${formData.isActive
                                  ? "translate-x-9"
                                  : "translate-x-1"
                                }`}
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {formData.isActive && activeCompanies.length > 0 && !editingCompany?.isActive && (
                  <div className="rounded-2xl border border-amber-400/30 bg-amber-500/10 px-4 py-3 flex items-start gap-3">
                    <FiAlertTriangle className="mt-1 text-amber-400" />
                    <p className="text-sm">
                      {isArabic
                        ? `الشركة النشطة الحالية هي: ${activeCompanies[0]?.deliveryCompanyName}. يجب تعطيلها أولًا قبل تفعيل هذه الشركة.`
                        : `Current active company: ${activeCompanies[0]?.deliveryCompanyName}. Disable it first before activating this one.`}
                    </p>
                  </div>
                )}

                <div
                  className={`rounded-[2rem] border p-4 sm:p-5 ${isDark
                      ? "bg-[#0B1020] border-[#334155]"
                      : "bg-gray-50 border-gray-200"
                    }`}
                >
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <div>
                      <h3 className="font-black">
                        {isArabic ? "رسوم المحافظات" : "City Fees"}
                      </h3>
                      <p className="text-xs opacity-70">
                        {isArabic
                          ? "كل محافظة يجب أن تحتوي على سعر."
                          : "Every city must have a fee."}
                      </p>
                    </div>
                    <span className="text-xs font-black opacity-60">
                      {formData.cities.length} {isArabic ? "محافظة" : "Cities"}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {formData.cities.map((city) => (
                      <div
                        key={city.cityId}
                        className={`rounded-2xl border p-4 ${isDark
                            ? "bg-[#111827] border-[#334155]"
                            : "bg-white border-gray-200"
                          }`}
                      >
                        <label className="block text-sm font-bold mb-2">
                          {city.cityName}
                        </label>
                        <input
                          type="text"
                          inputMode="decimal"
                          placeholder="0"
                          value={city.fees === "0" ? "" : city.fees}
                          onWheel={(e) => e.currentTarget.blur()}
                          onPaste={(e) => handleCityFeePaste(city.cityId, e)}
                          onChange={(e) =>
                            handleCityFeeChange(city.cityId, e.target.value)
                          }
                          className={`w-full p-3 rounded-xl border outline-none ${isDark
                              ? "bg-[#0B1020] border-[#334155]"
                              : "bg-gray-50 border-gray-200"
                            }`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full py-4 rounded-xl font-black shadow-lg transition-transform active:scale-[0.99]"
                  style={{ background: colors.mainColor, color: "white" }}
                >
                  {isArabic ? "حفظ" : "Save"}
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </section>
    </WhatsLayout>
  );
}
